import React, { PropsWithChildren, useMemo, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useGetOrdersByIds } from "@/services/Orders";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";

import styles from "./styles.module.css";
import Text from "@/components/atoms/Text";
import Group from "@/components/atoms/Group";
import CTA from "@/components/atoms/CTA";
import { Order, Task } from "@/types";
import { Grid } from "@mantine/core";
import { Control, Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  MultipleMixedFinalAmountFormInput,
  MultipleMixedFinalAmountValidationSchema,
} from "@/components/pages/Tasks/validation/finalAmountValidation";
import { useAuth } from "@/contexts/AuthContext";
import DrawerAccordion from "@/components/atoms/accordions/DrawerAccordion";
import DrawerTabs, { DrawerTab } from "@/components/atoms/DrawerTabs";
import Divider from "@/components/atoms/Divider";
import Badge from "@/components/atoms/Badge";
import NumberInput from "@/components/atoms/NumberInput";
import OrderProductDetails from "@/components/molecules/DrawerDetails/OrderProductDetails";
import DetailsRow from "@/components/molecules/DrawerDetails/DetailsRow";
import WeightInput from "@/components/molecules/WeightInput";
import FinalAmountWarningModal from "@/components/organisms/FinalAmountWarningModal";
import {
  FinalQuantityEntry,
  useFinalQuantitySave,
} from "@/components/organisms/FinalAmountWarningModal/useFinalQuantitySave";
import { weightUnitOf, WeightInputValue } from "@/utils/weight";

interface Props extends PropsWithChildren {
  task: Task;
  onClose: () => void;
}

/** The product as the task names it: "Cardboard | Clean", or just the name when the order has no property. */
const productLabelOf = (order: Order, languageId: number | undefined) => {
  const name = order.salesItem.nameTranslated?.[languageId as number] ?? order.salesItem.salesItem;
  const property = order.properties[0]?.value;
  return property ? `${name} | ${property}` : name;
};

// A kg or tonnes order holds a weight, and the others a plain amount, which the number field gives as "" when cleared.
const isWeightValue = (value: unknown): value is WeightInputValue => typeof value === "object" && value !== null;

/** The form value an order starts with: its estimate, as a weight in its own unit for kg and tonnes orders. */
const initialValueOf = (order: Order): WeightInputValue | number => {
  const unit = weightUnitOf(order.unit?.unit);
  return unit ? { amount: order.amount ?? "", unit } : (order.amount ?? 0);
};

export default function FinalAmountMultipleTask(props: Props) {
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);

  const initialAccordionSet = useRef(false);

  const { task, onClose } = props;
  const { t } = useTranslation();
  const { user } = useAuth();
  const { orders, isError: someOrdersFailed } = useGetOrdersByIds(task?.orderIds);

  const formId = "final-amount-form";

  const initialRecord = useMemo(() => {
    const defaults: MultipleMixedFinalAmountFormInput = {};

    if (task?.orderIds && (!orders || orders.length === 0)) {
      task.orderIds.forEach((id) => {
        defaults[id.toString()] = 0;
      });
      return defaults;
    }

    if (orders && orders.length > 0) {
      return orders.reduce((acc, order) => {
        acc[order.id.toString()] = initialValueOf(order);
        return acc;
      }, defaults);
    }

    return defaults;
  }, [orders, task?.orderIds]);

  const {
    handleSubmit,
    control,
    formState: { isValid },
    setError,
  } = useForm<MultipleMixedFinalAmountFormInput>({
    resolver: zodResolver(MultipleMixedFinalAmountValidationSchema),
    // The weight field has no blur event, so the form is checked as the amounts are typed.
    mode: "onChange",
    values: initialRecord,
  });

  useEffect(() => {
    if (!initialAccordionSet.current && orders && orders.length > 0) {
      setOpenAccordion(orders[0].id.toString());
      initialAccordionSet.current = true;
    }
  }, [orders, openAccordion]);

  // The save owns the notifications, the cache refresh and the backend's plausibility warning, and closes the task.
  const { save, isPending, fieldErrors, warningModal } = useFinalQuantitySave({
    mode: "batch",
    taskId: task.id,
    // The dialog names its orders the way the accordion does: "2041 · Cardboard".
    orderLabels: Object.fromEntries(
      orders.map((order) => [order.id, `${order.id} · ${productLabelOf(order, user?.language.id)}`])
    ),
    close: onClose,
  });

  // The backend's rejection of an order's weight (APP 189) opens the first order it names.
  useEffect(() => {
    const rejectedId = task.orderIds.find((id) => fieldErrors[id]);
    if (rejectedId !== undefined) setOpenAccordion(rejectedId.toString());
  }, [fieldErrors, task.orderIds]);

  const onSubmit = (data: MultipleMixedFinalAmountFormInput) => {
    const entries: FinalQuantityEntry[] = [];
    let hasDeviationError = false;

    for (const order of orders) {
      const orderId = order.id.toString();
      const value = data[orderId];

      // A weight is sent as typed, and the backend decides whether it is plausible.
      if (isWeightValue(value)) {
        entries.push({ orderId: order.id, weight: value });
        continue;
      }

      // Any other unit is held to the deviation the order agreed.
      const originalAmount = order.amount;
      const amountDeviation = order.amountDeviation || 0;

      const deviation: number = (originalAmount / 100) * amountDeviation;
      const lowEnd: number = originalAmount - deviation;
      const highEnd: number = originalAmount + deviation;

      if (value > highEnd || value < lowEnd) {
        hasDeviationError = true;

        setError(orderId, {
          type: "manual",
          message: t("tasks:legalFinalAmount") + lowEnd + order.unit?.unit + " - " + highEnd + order.unit?.unit,
        });
        setOpenAccordion(orderId);
        continue;
      }

      entries.push({ orderId: order.id, amount: value });
    }

    if (hasDeviationError) {
      return;
    }
    save(entries);
  };

  // Every order of the task goes in the one request, so Save waits until they have all loaded.
  const allOrdersLoaded = orders.length === task.orderIds.length;

  return (
    <>
      <DrawerTabs defaultValue="orders" className="full flex col between h-100 py-10">
        <Group gap="20px" align="center" className={styles.taskHeader}>
          <SVGAsset name={orders[0]?.orderType.type === "BUY" ? "purchaseIndicator" : "saleIndicator"} />
          <Text size="md" display="technical" tt="uppercase" strong={true}>
            {t("enumerables:taskTypes." + task?.type)}
          </Text>
        </Group>

        {/* Form with ID to connect to external submit button */}
        <DrawerTabs.List>
          <DrawerTabs.Tab value="orders">
            <DrawerTab svgName="hamburger" label={t("tasks:orders")} />
          </DrawerTabs.Tab>
        </DrawerTabs.List>
        <DrawerTabs.Panel value="orders" className={styles.tabPanel}>
          <form id={formId} onSubmit={handleSubmit(onSubmit)} className={styles.tabPanel}>
            <DrawerAccordion unstyled={true} value={openAccordion} onChange={setOpenAccordion}>
              {orders &&
                orders.map((order) => {
                  const productLabel = productLabelOf(order, user?.language.id);
                  return (
                    <DrawerAccordion.Item key={order.id} value={order.id.toString()}>
                      <DrawerAccordion.Control>
                        <Text size="sm" display="technical" tt="uppercase" strong={true}>
                          {order.id} | {productLabel}
                        </Text>
                      </DrawerAccordion.Control>
                      <DrawerAccordion.Panel>
                        <Grid>
                          <OrderProductDetails
                            defaultProductName={`${order.salesItem.nameTranslated?.[user?.language.id as number] ?? order.salesItem.salesItem} - ${order.properties[0] ? order.properties[0].value : ""}`}
                            product={order.companySpecificProductDto}
                          />
                          <DetailsRow
                            label={"orders:amountOrdered"}
                            value={`${order.amount} ${order.unit?.unit}`}
                            isLoading={false}
                          />
                          <Grid.Col span={12}>
                            <Divider />
                          </Grid.Col>
                          <FinalAmount order={order} control={control} saveError={fieldErrors[order.id]} />
                        </Grid>
                      </DrawerAccordion.Panel>
                    </DrawerAccordion.Item>
                  );
                })}
            </DrawerAccordion>
            {/* Save stays off until every order has loaded, so say why when one cannot be. */}
            {someOrdersFailed && (
              <Text size="sm" color="error" p={15} data-testid="final-amount-multiple-orders-not-loaded">
                {t("tasks:ordersNotLoaded")}
              </Text>
            )}
          </form>
        </DrawerTabs.Panel>
        <Group className={styles.tabActionGroup} p={15} gap={16} align="center" justify="flex-end">
          <CTA
            data-testid="final-amount-multiple-submit-btn"
            intent="primary"
            size="sm"
            fullWidth={false}
            type="submit"
            form={formId}
            disabled={!isValid || !allOrdersLoaded || isPending}
            loading={isPending}
          >
            {t("actions:save")}
          </CTA>
          <CTA intent="secondary" size="sm" fullWidth={false} type="button" onClick={onClose} disabled={isPending}>
            {t("actions:shutdown")}
          </CTA>
        </Group>
      </DrawerTabs>
      <FinalAmountWarningModal {...warningModal} />
    </>
  );
}

interface FinalAmountProps {
  order: Order;
  control: Control<MultipleMixedFinalAmountFormInput>;
  /** The translation key of the backend's rejection of this order's weight. A validation message goes first. */
  saveError?: string;
}

/** The final amount row of one order: the weight field for a kg or tonnes order, the plain amount field for the others. */
const FinalAmount: React.FC<FinalAmountProps> = ({ order, control, saveError }) => {
  const { t } = useTranslation();

  return (
    <>
      <Grid.Col span={3}>
        <Text size="sm" c="dimmed">
          {t("tasks:finalAmount")}
        </Text>
      </Grid.Col>
      <Grid.Col span={9}>
        <Group wrap={"nowrap"}>
          <Controller
            control={control}
            name={order.id.toString()}
            render={({ field, fieldState }) =>
              isWeightValue(field.value) ? (
                <div style={{ flex: 1, minWidth: 0 }}>
                  <WeightInput
                    data-testid={`final-amount-input-${order.id}`}
                    aria-label={t("tasks:finalAmount")}
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error ? t(fieldState.error.message as string) : saveError && t(saveError)}
                  />
                </div>
              ) : (
                <span
                  style={{
                    position: "relative",
                    display: "inline-block",
                    overflow: "hidden",
                  }}
                >
                  <NumberInput
                    data-testid={`final-amount-input-${order.id}`}
                    hideControls
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={
                      fieldState.error
                        ? fieldState.error.type === "manual"
                          ? fieldState.error.message
                          : t(fieldState.error.message as string)
                        : undefined
                    }
                  />
                  <Badge size={"sm"} h={35} radius={0} color={"primary"} pos={"absolute"} top={0} right={0} bottom={0}>
                    {order.unit?.unit}
                  </Badge>
                </span>
              )
            }
          />
        </Group>
      </Grid.Col>
    </>
  );
};
